<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import {
  useFunctionGetter,
  useMapGetter,
  useStore,
} from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import ButtonNext from 'next/button/Button.vue';
import googleContactsClient from 'dashboard/api/google_contacts_auth.js';

import Integration from './Integration.vue';
import Spinner from 'shared/components/Spinner.vue';

const { t } = useI18n();
const store = useStore();
const integrationLoaded = ref(false);

const integration = useFunctionGetter('integrations/getIntegration', 'google_contacts');

const uiFlags = useMapGetter('integrations/getUIFlags');

const integrationAction = computed(() => {
  if (integration.value.enabled) {
    return 'disconnect';
  }

  return '';
});

const authorize = async () => {
  try {
    const response = await googleContactsClient.generateAuthorization();
    const {
      data: { url },
    } = response;

    window.location.href = url;
  } catch (error) {
    // Error handling could be added here
  }
};

const initializeGoogleContactsIntegration = async () => {
  await store.dispatch('integrations/get', 'google_contacts');
  integrationLoaded.value = true;
};

onMounted(() => {
  initializeGoogleContactsIntegration();
});

const spreadsheetId = ref('');
const sheetName = ref('');
const isSaving = ref(false);

const currentHook = computed(() => {
  if (!integration.value) return {};
  const { hooks = [] } = integration.value;
  return hooks.length > 0 ? hooks[0] : {};
});

const hasChanges = computed(() => {
  return spreadsheetId.value !== (currentHook.value.settings?.spreadsheet_id || '') ||
         sheetName.value !== (currentHook.value.settings?.sheet_name || '');
});

watch(currentHook, (newHook) => {
  if (newHook && newHook.settings) {
    if (newHook.settings.spreadsheet_id) {
      spreadsheetId.value = newHook.settings.spreadsheet_id;
    }
    if (newHook.settings.sheet_name) {
      sheetName.value = newHook.settings.sheet_name;
    }
  }
}, { immediate: true, deep: true });

const saveSettings = async () => {
  if (!currentHook.value || !currentHook.value.id) {
     isSaving.value = false;
     return;
  }

  isSaving.value = true;
  try {
    await store.dispatch('integrations/update', {
      id: currentHook.value.id,
      settings: {
        ...currentHook.value.settings,
        spreadsheet_id: spreadsheetId.value,
        sheet_name: sheetName.value,
      },
    });
    useAlert(t('INTEGRATION_SETTINGS.GOOGLE_CONTACTS.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.GOOGLE_CONTACTS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div class="flex-grow flex-shrink p-4 overflow-auto mx-auto">
    <div v-if="integrationLoaded">
      <Integration
        :integration-id="integration.id"
        :integration-logo="integration.logo"
        :integration-name="integration.name"
        :integration-description="integration.description"
        :integration-enabled="integration.enabled"
        :integration-action="integrationAction"
        :delete-confirmation-text="{
          title: t('INTEGRATION_SETTINGS.GOOGLE_CONTACTS.DELETE.TITLE'),
          message: t('INTEGRATION_SETTINGS.GOOGLE_CONTACTS.DELETE.MESSAGE'),
        }"
      >
        <template #action>
          <ButtonNext
            v-if="!integration.enabled"
            faded
            blue
            :label="t('INTEGRATION_SETTINGS.CONNECT.BUTTON_TEXT')"
            @click="authorize"
          />
        </template>
        <template #settings>
          <div
            v-if="integration.enabled"
            class="border-t border-n-weak p-6 bg-n-alpha-1 flex flex-col gap-2 rounded-b-md"
          >
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('INTEGRATION_SETTINGS.GOOGLE_CONTACTS.SPREADSHEET_ID_LABEL') }}
            </label>
            <div class="flex gap-2 items-center w-full max-w-2xl">
              <input
                v-model="spreadsheetId"
                type="text"
                class="flex-1 rounded-md border-n-weak bg-n-alpha-1 px-3 py-1.5 text-sm focus:border-n-brand focus:ring-n-brand"
                placeholder="Ex: 1BxiMVs0..."
              />
            </div>
            <p class="text-xs text-n-slate-11">
              {{ t('INTEGRATION_SETTINGS.GOOGLE_CONTACTS.SPREADSHEET_HELP') }}
            </p>

            <label class="text-sm font-medium text-n-slate-12 mt-2">
              {{ t('INTEGRATION_SETTINGS.GOOGLE_CONTACTS.SHEET_NAME_LABEL') }}
            </label>
            <div class="flex gap-2 items-center w-full max-w-2xl">
              <input
                v-model="sheetName"
                type="text"
                class="flex-1 rounded-md border-n-weak bg-n-alpha-1 px-3 py-1.5 text-sm focus:border-n-brand focus:ring-n-brand"
                placeholder="Ex: Hoja 1"
              />
            </div>
            <div class="flex flex-row-reverse w-full max-w-2xl mt-2">
              <ButtonNext
                v-if="hasChanges"
                :is-loading="isSaving"
                :label="t('INTEGRATION_SETTINGS.GOOGLE_CONTACTS.SAVE_BUTTON')"
                @click="saveSettings"
              />
            </div>
          </div>
        </template>
      </Integration>
    </div>
    <div v-else class="flex items-center justify-center flex-1">
      <Spinner size="" color-scheme="primary" />
    </div>
  </div>
</template>
